import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Hospital, LogIn, LogOut, Clock, Building2, FileText } from 'lucide-react';
import { format, parseISO, differenceInHours, differenceInDays } from 'date-fns';

interface HospitalizationEvent {
  id: string;
  event_type: string;
  event_timestamp: string;
  facility_name: string | null;
  admission_reason: string | null;
  notes: string | null;
  created_at: string;
}

interface HospitalizationHistoryProps {
  patientId: string;
}

interface StayPeriod {
  admission: HospitalizationEvent;
  discharge: HospitalizationEvent | null;
  durationHours: number | null;
  isOngoing: boolean;
}

export function HospitalizationHistory({ patientId }: HospitalizationHistoryProps) {
  const [events, setEvents] = useState<HospitalizationEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvents();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('hospitalization-history')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'patient_hospitalization_events',
          filter: `patient_id=eq.${patientId}`
        },
        () => loadEvents()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [patientId]);

  const loadEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('patient_hospitalization_events')
        .select('*')
        .eq('patient_id', patientId)
        .order('event_timestamp', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error loading hospitalization events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stay periods by matching admissions with discharges
  const calculateStayPeriods = (): StayPeriod[] => {
    const periods: StayPeriod[] = [];
    const sortedEvents = [...events].sort((a, b) => 
      new Date(a.event_timestamp).getTime() - new Date(b.event_timestamp).getTime()
    );

    let currentAdmission: HospitalizationEvent | null = null;

    for (const event of sortedEvents) {
      if (event.event_type === 'admission') {
        // If there's an unclosed admission, close it as ongoing
        if (currentAdmission) {
          periods.push({
            admission: currentAdmission,
            discharge: null,
            durationHours: null,
            isOngoing: true
          });
        }
        currentAdmission = event;
      } else if (event.event_type === 'discharge' && currentAdmission) {
        const hours = differenceInHours(
          parseISO(event.event_timestamp),
          parseISO(currentAdmission.event_timestamp)
        );
        periods.push({
          admission: currentAdmission,
          discharge: event,
          durationHours: hours,
          isOngoing: false
        });
        currentAdmission = null;
      }
    }

    // Add any remaining open admission
    if (currentAdmission) {
      const hoursOngoing = differenceInHours(new Date(), parseISO(currentAdmission.event_timestamp));
      periods.push({
        admission: currentAdmission,
        discharge: null,
        durationHours: hoursOngoing,
        isOngoing: true
      });
    }

    // Return in reverse chronological order
    return periods.reverse();
  };

  const formatDuration = (hours: number | null, isOngoing: boolean): string => {
    if (hours === null) return 'Unknown';
    
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    
    let durationStr = '';
    if (days > 0) {
      durationStr += `${days}d `;
    }
    durationStr += `${remainingHours}h`;
    
    if (isOngoing) {
      durationStr += ' (ongoing)';
    }
    
    return durationStr;
  };

  const totalHospitalizations = calculateStayPeriods().length;
  const ongoingAdmissions = calculateStayPeriods().filter(p => p.isOngoing).length;
  const totalHoursLost = calculateStayPeriods()
    .filter(p => p.durationHours !== null)
    .reduce((sum, p) => sum + (p.durationHours || 0), 0);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  const stayPeriods = calculateStayPeriods();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Hospital className="h-5 w-5 text-primary" />
          Hospitalization History
        </CardTitle>
        <CardDescription>
          Admission and discharge events with duration tracking
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center p-3 rounded-lg bg-muted/50">
            <p className="text-2xl font-bold text-foreground">{totalHospitalizations}</p>
            <p className="text-xs text-muted-foreground">Total Stays</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-muted/50">
            <p className="text-2xl font-bold text-foreground">{totalHoursLost}</p>
            <p className="text-xs text-muted-foreground">Hours Lost</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-muted/50">
            <p className={`text-2xl font-bold ${ongoingAdmissions > 0 ? 'text-destructive' : 'text-foreground'}`}>
              {ongoingAdmissions}
            </p>
            <p className="text-xs text-muted-foreground">Currently Admitted</p>
          </div>
        </div>

        {stayPeriods.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Hospital className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No hospitalization events recorded</p>
          </div>
        ) : (
          <div className="space-y-4">
            {stayPeriods.map((period, index) => (
              <div
                key={period.admission.id}
                className={`p-4 rounded-lg border ${
                  period.isOngoing 
                    ? 'border-destructive/50 bg-destructive/5' 
                    : 'border-border'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Hospital className={`h-5 w-5 ${period.isOngoing ? 'text-destructive' : 'text-primary'}`} />
                    <span className="font-medium text-foreground">
                      {period.admission.facility_name || 'Hospital/ER Visit'}
                    </span>
                    {period.isOngoing && (
                      <Badge variant="destructive" className="text-xs">
                        Currently Admitted
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span className="font-medium">
                      {formatDuration(period.durationHours, period.isOngoing)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Admission */}
                  <div className="flex items-start gap-3 p-3 rounded-md bg-muted/30">
                    <div className="p-1.5 rounded-full bg-amber-100 dark:bg-amber-900/30">
                      <LogIn className="h-4 w-4 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">Admitted</p>
                      <p className="text-sm text-muted-foreground">
                        {format(parseISO(period.admission.event_timestamp), 'MMM d, yyyy h:mm a')}
                      </p>
                      {period.admission.admission_reason && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Reason: {period.admission.admission_reason}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Discharge */}
                  <div className="flex items-start gap-3 p-3 rounded-md bg-muted/30">
                    <div className={`p-1.5 rounded-full ${
                      period.discharge 
                        ? 'bg-green-100 dark:bg-green-900/30' 
                        : 'bg-muted'
                    }`}>
                      <LogOut className={`h-4 w-4 ${
                        period.discharge ? 'text-green-600' : 'text-muted-foreground'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        {period.discharge ? 'Discharged' : 'Awaiting Discharge'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {period.discharge 
                          ? format(parseISO(period.discharge.event_timestamp), 'MMM d, yyyy h:mm a')
                          : 'Patient currently hospitalized'
                        }
                      </p>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {(period.admission.notes || period.discharge?.notes) && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div className="text-sm text-muted-foreground">
                        {period.admission.notes && <p>Admission: {period.admission.notes}</p>}
                        {period.discharge?.notes && <p>Discharge: {period.discharge.notes}</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
